/**
 * Tax / cart totals — see src/utils/financeEngine.js for precise arithmetic.
 */
import {
  calculateCartTotals as engineCalculateCartTotals,
  calculateItemTaxRow,
  calculateReturnItemTaxRow,
  getBillDiscountRate,
  getItemLineAfterBillDiscount,
  computeItemTaxOnExclusiveTaxable,
  roundMoneyHalfUp,
  toDecimal,
  toMoneyNumber,
  convertCurrency as convertCurrencyAmount,
} from "../../../../utils/financeEngine";

export const calculateCartTotals = engineCalculateCartTotals;

export const calculateItemTax = (item, pharmacyStateCode, patientStateCode, posTaxType) =>
  calculateItemTaxRow(item, pharmacyStateCode, patientStateCode, posTaxType);

export const calculateReturnItemTax = (item, pharmacyStateCode, patientStateCode, posTaxType) => {
  if (item.taxPercentage !== undefined) {
    return calculateReturnItemTaxRow(item, pharmacyStateCode, patientStateCode, posTaxType);
  }
  return calculateItemTaxRow(item, pharmacyStateCode, patientStateCode, posTaxType);
};

/**
 * Build invoice payload with tax data
 * @param {string} [sourceCurrency] - Currency of cart totals (must match `totals` units). Defaults to first allowed or base.
 * @param {Object} [conversionRates] - from pharmacy — required for correct FX in payload
 */
export const buildInvoicePayloadWithTax = ({
  cart,
  itemTaxes,
  discountType,
  discountValue,
  transportCost,
  paymentData,
  paidType,
  customerType,
  selectedPatient,
  pharmacyPOS,
  posTaxType,
  pharmacyStateCode,
  patientStateCode,
  convertCurrency: convertCurrencyProp,
  sourceCurrency: sourceCurrencyParam,
  doctorId,
  note,
  prescriptionDate,
  t,
}) => {
  const isSameState =
    !patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

  const rates = pharmacyPOS?.conversionRates;
  const sourceCurrency =
    sourceCurrencyParam ||
    pharmacyPOS?.baseCurrency ||
    pharmacyPOS?.allowedCurrencies?.[0] ||
    "INR";
  const payCur = paymentData.currency || sourceCurrency;

  const totals = engineCalculateCartTotals(
    cart,
    itemTaxes,
    discountType,
    discountValue,
    transportCost,
    posTaxType,
    isSameState
  );

  const netTotalInPaymentCurrency = toMoneyNumber(
    typeof convertCurrencyProp === "function"
      ? toDecimal(convertCurrencyProp(totals.netTotal, sourceCurrency, payCur)).toNumber()
      : convertCurrencyAmount(totals.netTotal, sourceCurrency, payCur, rates).toNumber()
  );

  const billDiscountRate = getBillDiscountRate(totals.subtotal, totals.discount);

  let maxTaxPercentage = 0;
  Object.values(itemTaxes).forEach((taxInfo) => {
    if (taxInfo.taxPercentage > maxTaxPercentage) {
      maxTaxPercentage = taxInfo.taxPercentage;
    }
  });

  const toFixed2 = (n) => roundMoneyHalfUp(n).toFixed(2);

  const rawTotalPaid = toMoneyNumber(Number(paymentData.amount) || 0);
  const totalPaid =
    paidType === "full"
      ? netTotalInPaymentCurrency
      : toMoneyNumber(Math.min(rawTotalPaid, netTotalInPaymentCurrency));

  return {
    clinicId: pharmacyPOS?.hospital_id,
    clinicName: pharmacyPOS?.hospital?.name || "",
    pharmacyId: pharmacyPOS?._id,
    pharmacyName: pharmacyPOS?.facility_name,
    patientId: customerType !== "walking" ? selectedPatient?._id : null,
    isHospitalConnected: customerType === "hospital_patient",
    patientName: paymentData.patientName || selectedPatient?.name || t("Walk-in Customer"),
    discountType: discountType.toLowerCase(),
    discount: discountValue || 0,
    transportCost: transportCost || 0,
    netTotal: netTotalInPaymentCurrency,
    otherNetTotalPrice: 0,
    currencyType: paymentData.currency || "INR",
    paymentType: paymentData.type,
    isConversion: paymentData.currency !== sourceCurrency,
    customerType: customerType,
    totalPaid,
    isPartialPaid: paidType === "partial",
    isPaid: paidType === "full",
    zigConversionValue: paymentData.zigConversionValue || 1,
    isSettled: paymentData.isSettle || false,
    ...(doctorId ? { doctorId } : {}),
    ...(note ? { note } : {}),
    ...(prescriptionDate ? { prescriptionDate } : {}),

    isIGST: !isSameState && posTaxType === "gst",
    IGST: toFixed2(totals.IGST),
    taxPercentage: maxTaxPercentage,
    tax: toFixed2(totals.tax),
    SGST: toFixed2(totals.SGST),
    CGST: toFixed2(totals.CGST),
    IGSTPercentage: !isSameState && posTaxType === "gst" ? maxTaxPercentage : 0,
    CGSTPercentage: isSameState && posTaxType === "gst" ? maxTaxPercentage / 2 : 0,
    SGSTPercentage: isSameState && posTaxType === "gst" ? maxTaxPercentage / 2 : 0,

    items: cart.map((item) => {
      const itemKey = `${item._id}-${item.selectedBatchId}`;
      const taxInfo = itemTaxes[itemKey] || {};
      const taxableBeforeDiscount = toDecimal(taxInfo.taxableAmount || 0);
      const afterDisc = getItemLineAfterBillDiscount(taxableBeforeDiscount, billDiscountRate);
      const taxRate = taxInfo.taxPercentage || 0;
      const { itemTax, itemCGST, itemSGST, itemIGST, itemVAT } = computeItemTaxOnExclusiveTaxable(
        afterDisc,
        taxRate,
        posTaxType,
        isSameState
      );

      return {
        medicineQunatity: item.quantity,
        stockId: item.selectedBatchId,
        itemId: item._id,
        batchNumber: item.batchNumber,
        productForm: item.productForm,
        medicineName: item.saltName,
        manufacturer: item.manufacturer,
        discount: item.discount || 0,
        brandName: item.brandName,
        isOTC: item.isOTC || false,
        isUnani: item.isUnani || false,
        MRP: toFixed2(item.salePrice),
        sellingPrice: toFixed2(item.salePrice),
        grossTotal: toFixed2(item.grossTotal),
        isIGST: !isSameState && posTaxType === "gst",
        IGST: toFixed2(itemIGST),
        taxPercentage: taxInfo.taxPercentage || 0,
        tax: toFixed2(itemTax),
        SGST: toFixed2(itemSGST),
        CGST: toFixed2(itemCGST),
        VAT: toFixed2(itemVAT),
        IGSTPercentage: !isSameState && posTaxType === "gst" ? (taxInfo.taxPercentage || 0) : 0,
        CGSTPercentage: isSameState && posTaxType === "gst" ? (taxInfo.taxPercentage || 0) / 2 : 0,
        SGSTPercentage: isSameState && posTaxType === "gst" ? (taxInfo.taxPercentage || 0) / 2 : 0,
      };
    }),
  };
};
